import React from "react";
import {
  Banner,
  Button,
  EmptyState,
  Flex,
  LoadingIndicator,
  NumberedPagination,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tag,
  Text,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DocumentUpload } from "../DocumentUpload";
import {
  formatDocumentType,
  formatFieldsExtracted,
  formatReceivedAt,
  formatSource,
  formatStage,
  statusLabel,
  statusTagVariant,
} from "./format";
import * as styles from "./IncomingUploads.css";
import type { PipelineStatusRow } from "../../hooks/usePipelineStatuses";

export const INCOMING_UPLOADS_COPY = {
  heading: "Documents",
  refresh: "Refresh",
  emptyTitle: "No documents yet",
  emptyDescription: "Uploaded documents and their extraction status will appear here.",
  errorTitle: "Couldn't load uploads",
  errorDescription: "Something went wrong fetching the pipeline. Try refreshing.",
} as const;

const COLUMNS = [
  "File name",
  "Document type",
  "Source",
  "Stage",
  "Status",
  "Fields extracted",
  "Received",
] as const;

export interface IncomingUploadsProps {
  statuses: PipelineStatusRow[];
  loading: boolean;
  error?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRefresh: () => void;
  onRowSelect?: (documentId: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number | null) => void;
}

export const IncomingUploads = ({
  statuses,
  loading,
  error = false,
  onUpload,
  onRefresh,
  onRowSelect,
  page,
  totalPages,
  onPageChange,
}: IncomingUploadsProps) => {
  const hasRows = statuses.length > 0;

  const renderContent = () => {
    if (!hasRows && loading) {
      return (
        <Flex
          justifyContent="center"
          className={styles.loadingWrap}
          data-testid="uploads-loading"
        >
          <LoadingIndicator />
        </Flex>
      );
    }
    if (!hasRows) {
      return (
        <EmptyState
          illustration="casual-documents"
          title={INCOMING_UPLOADS_COPY.emptyTitle}
          description={INCOMING_UPLOADS_COPY.emptyDescription}
        />
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col} header rightAligned={col === "Fields extracted"}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {statuses.map((row) => (
            <TableRow
              key={row.documentId}
              data-testid={`upload-row-${row.documentId}`}
              onClick={onRowSelect ? () => onRowSelect(row.documentId) : undefined}
            >
              <TableCell>{row.originalFilename}</TableCell>
              <TableCell>{formatDocumentType(row.documentType)}</TableCell>
              <TableCell>{formatSource(row.sourceId)}</TableCell>
              <TableCell>{formatStage(row.stage)}</TableCell>
              <TableCell>
                <Tag variant={statusTagVariant(row.status)} label={statusLabel(row.status)} />
              </TableCell>
              <TableCell rightAligned>{formatFieldsExtracted(row.fieldsExtracted)}</TableCell>
              <TableCell>{formatReceivedAt(row.receivedAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Flex flexDirection="column" gap={Sizes.LG}>
      <DocumentUpload onUpload={onUpload} />
      <section aria-label={INCOMING_UPLOADS_COPY.heading}>
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text variant="h2">{INCOMING_UPLOADS_COPY.heading}</Text>
            <Button
              variant="neutral"
              icon="arrows-rotate"
              iconPosition="left"
              isLoading={loading}
              onClick={onRefresh}
              data-testid="refresh-uploads"
            >
              {INCOMING_UPLOADS_COPY.refresh}
            </Button>
          </Flex>
          {error && (
            <Banner
              variant="danger"
              title={INCOMING_UPLOADS_COPY.errorTitle}
              description={INCOMING_UPLOADS_COPY.errorDescription}
            />
          )}
          {/* Keep showing cached rows on a refresh error — the banner sits above them. */}
          {(hasRows || !error) && renderContent()}
          {totalPages > 1 && (
            <NumberedPagination
              total={totalPages}
              activePage={page}
              // DS `disabled` styles the control but still fires page-button clicks; guard here.
              handleClick={(nextPage) => {
                if (!loading) onPageChange(nextPage);
              }}
              disabled={loading}
              aria-label="Pagination"
              data-testid="uploads-pagination"
            />
          )}
        </Flex>
      </section>
    </Flex>
  );
};
